/**
 * ChallengeDetail Page
 *
 * The writing interface for a specific challenge.
 * Creates/loads a submission and provides the editor.
 */

import { useState, useEffect, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronLeft, Target, Star, Sparkles, Loader2 } from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { ChallengeWriter, CharacterPicker } from '@/components/challenge'
import { useFamily } from '@/context/FamilyContext'
import { useToast } from '@/context/ToastContext'
import {
  getChallenge,
  getSubmissionsForChallenge,
  createSubmission,
  updateSubmissionContent,
  updateSubmissionCharacter,
  submitForAnalysis,
} from '@/services/challengeService'
import type { LibraryCharacter } from '@/types/database'
import type { StoryChallenge, ChallengeSubmission, ChallengeDifficulty } from '@/types/database'

const DIFFICULTY_CONFIG: Record<
  ChallengeDifficulty,
  { label: string; color: string; icon: typeof Star }
> = {
  beginner: {
    label: 'Beginner',
    color: 'text-[var(--color-kids-success)]',
    icon: Star,
  },
  intermediate: {
    label: 'Intermediate',
    color: 'text-[var(--color-kids-secondary)]',
    icon: Sparkles,
  },
  advanced: {
    label: 'Advanced',
    color: 'text-[var(--color-kids-accent)]',
    icon: Target,
  },
}

export function ChallengeDetail() {
  const { challengeId } = useParams<{ challengeId: string }>()
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const { showToast } = useToast()

  const [challenge, setChallenge] = useState<StoryChallenge | null>(null)
  const [submission, setSubmission] = useState<ChallengeSubmission | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Load challenge and submission
  useEffect(() => {
    async function loadData() {
      if (!challengeId || !activeWriter) return

      setIsLoading(true)
      try {
        // Load challenge
        const challengeData = await getChallenge(challengeId)
        if (!challengeData) {
          showToast('Challenge not found', 'error')
          navigate('/challenge')
          return
        }
        setChallenge(challengeData)

        // Load or create submission
        const submissions = await getSubmissionsForChallenge(
          activeWriter.id,
          challengeId
        )

        // Find an existing draft or create new
        const draftSubmission = submissions.find(s => s.status === 'draft')
        if (draftSubmission) {
          setSubmission(draftSubmission)
        } else {
          // Create new submission
          const newSubmission = await createSubmission(activeWriter.id, challengeId)
          setSubmission(newSubmission)
        }
      } catch (error) {
        console.error('Error loading challenge:', error)
        showToast('Failed to load challenge', 'error')
        navigate('/challenge')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [challengeId, activeWriter, navigate, showToast])

  // Handle character selection
  const handleCharacterSelect = useCallback(
    async (character: LibraryCharacter | null) => {
      if (!submission) return
      try {
        const updated = await updateSubmissionCharacter(
          submission.id,
          character?.id || null
        )
        setSubmission(updated)
      } catch (error) {
        console.error('Error updating character:', error)
        showToast('Failed to update character', 'error')
      }
    },
    [submission, showToast]
  )

  // Handle content save
  const handleSave = useCallback(
    async (content: string) => {
      if (!submission) return
      const updated = await updateSubmissionContent(submission.id, content)
      setSubmission(updated)
    },
    [submission]
  )

  // Handle submit for analysis
  const handleSubmit = useCallback(async () => {
    if (!submission) return

    setIsSubmitting(true)
    try {
      const { submission: updated } = await submitForAnalysis(submission.id)
      showToast('Story submitted! Check your feedback.', 'success')
      navigate(`/challenge/${challengeId}/submission/${updated.id}`)
    } catch (error) {
      console.error('Error submitting:', error)
      showToast('Failed to submit. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }, [submission, challengeId, navigate, showToast])

  if (isLoading || !challenge || !submission) {
    return (
      <div className="min-h-screen bg-[var(--color-kids-bg)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-kids-primary)]" />
      </div>
    )
  }

  const difficultyConfig = DIFFICULTY_CONFIG[challenge.difficulty]
  const DifficultyIcon = difficultyConfig.icon

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-surface-alt)] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/challenge">
              <Button variant="ghost" size="sm">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="font-display font-bold text-lg text-[var(--color-kids-text)] truncate">
                {challenge.title}
              </h1>
              <div className="flex items-center gap-2 text-sm">
                <DifficultyIcon className={`w-4 h-4 ${difficultyConfig.color}`} />
                <span className={difficultyConfig.color}>
                  {difficultyConfig.label}
                </span>
                <span className="text-[var(--color-kids-text-muted)]">
                  • {challenge.word_goal_min}–{challenge.word_goal_max} words
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Challenge Info */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <p className="text-[var(--color-kids-text)]">
                {challenge.description}
              </p>
              {challenge.required_elements.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[var(--color-kids-surface-alt)]">
                  <p className="text-sm text-[var(--color-kids-text-muted)] mb-2">
                    Try to include these story elements:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {challenge.required_elements.map(element => (
                      <span
                        key={element}
                        className="text-sm bg-[var(--color-kids-primary)]/10 text-[var(--color-kids-primary)] px-3 py-1 rounded-full"
                      >
                        {element.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Character Picker - optional */}
          {activeWriter && (
            <CharacterPicker
              childId={activeWriter.id}
              selectedCharacterId={submission.character_id}
              onSelect={handleCharacterSelect}
              disabled={submission.status !== 'draft'}
            />
          )}

          {/* Writer */}
          <ChallengeWriter
            challenge={challenge}
            submission={submission}
            onSave={handleSave}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </motion.div>
      </main>
    </div>
  )
}

export default ChallengeDetail
